import { Injectable, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BASE_SITE_CONTEXT_ID, StatePersistenceService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, filter, map } from 'rxjs/operators';
import { EntriesState } from '../../../../core/features/store/base-feature-state';
import { AlertType } from '../../../../core/model';
import { SiteContextParamsService } from '../../../../core/site-context';
import { AlertsActions } from '../store/actions';
import { DismissalState, StateWithAlerts } from '../store/alerts-state';
import { AlertsSelectors } from '../store/selectors';

@Injectable({
  providedIn: 'root',
})
export class AlertsStatePersistenceService implements OnDestroy {
  protected subscription = new Subscription();

  constructor(
    private statePersistenceService: StatePersistenceService,
    private store: Store<StateWithAlerts>,
    private siteContextParamsService: SiteContextParamsService
  ) {}

  public initSync() {
    this.subscription.add(
      this.statePersistenceService.syncWithStorage({
        key: 'alerts',
        state$: this.getAlertsState(),
        context$: this.siteContextParamsService.getValues([BASE_SITE_CONTEXT_ID]),
        onRead: (state) => this.onRead(state),
      })
    );
  }

  protected getAlertsState(): Observable<{ dismissals: EntriesState<DismissalState> }> {
    return this.store.pipe(
      select(AlertsSelectors.getAlertsState),
      filter((state) => !!state),
      distinctUntilKeyChanged('dismissals'),
      map((state) => {
        return {
          dismissals: state.dismissals,
        };
      })
    );
  }

  protected onRead(state: { dismissals: EntriesState<DismissalState> } | undefined) {
    if (state) {
      for (const dismissal in state.dismissals) {
        if (!state.dismissals.hasOwnProperty(dismissal)) {
          continue;
        }

        const alertTypeKey = Object.keys(AlertType).find((key) => AlertType[key] === dismissal);
        const alertType = AlertType[alertTypeKey];

        this.store.dispatch(
          new AlertsActions.SetAlert({
            type: alertType,
            dismissed: state.dismissals[dismissal].dismissed,
            reappearable: state.dismissals[dismissal].reappearable,
            messageHash: state.dismissals[dismissal].messageHash,
          })
        );
        // Do not care if the page was reloaded just before reappearing, it will just take longer until next reappear
        if (state.dismissals[dismissal].reappearable) {
          this.store.dispatch(new AlertsActions.ClearAlertDelayed({ type: alertType }));
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
